import { useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Baby, Phone, Pill, AlertTriangle, Siren, Camera, Images, Award, Pencil, DoorOpen, GraduationCap, Check, Search, Filter, X, IdCard, History, Bookmark, Trash2, Printer, Globe, Lock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import ChildPhoto from "@/components/ChildPhoto";
import ChildGalleryDialog from "@/components/ChildGalleryDialog";
import ProgressLineChart from "@/components/ProgressLineChart";
import CertificateHistoryDialog from "@/components/CertificateHistoryDialog";
import BulkRoomAssignDialog from "@/components/BulkRoomAssignDialog";

const empty = {
  name: "", date_of_birth: "", gender: "male", disability_type: "",
  medications: "", allergies: "", emergency_contact_name: "", emergency_contact_phone: "",
  parent_id: "", assigned_teacher_ids: [], notes: "", room_number: null,
};

const ROOM_OPTIONS = [1, 2, 3, 4, 5, 6];

export default function ChildrenPage() {
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  const [parents, setParents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null); // child object or null
  const [emergency, setEmergency] = useState(null);
  const [galleryFor, setGalleryFor] = useState(null);
  const [certHistoryFor, setCertHistoryFor] = useState(null);
  const [bulkRoomOpen, setBulkRoomOpen] = useState(false);
  const [q, setQ] = useState("");
  const [roomFilter, setRoomFilter] = useState("all"); // all|1..6|none
  const [teacherFilter, setTeacherFilter] = useState("all"); // all|teacherId|none
  const [presets, setPresets] = useState([]);
  const [roomHistoryFor, setRoomHistoryFor] = useState(null); // child
  const canTriggerEmergency = user.role === "admin" || user.role === "teacher";

  const load = async () => {
    const { data } = await api.get("/children");
    setChildren(data);
    if (user.role === "admin") {
      const [p, t] = await Promise.all([
        api.get("/users?role=parent"),
        api.get("/users?role=teacher"),
      ]);
      setParents(p.data);
      setTeachers(t.data);
      try {
        const pr = await api.get("/admin/filter-presets", { params: { scope: "students" } });
        setPresets(pr.data || []);
      } catch { /* non-critical */ }
    }
  };

  useEffect(() => { load(); }, []);

  // Auto-refresh when tab regains focus / becomes visible again
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", load);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", load);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const teacherById = (id) => teachers.find((t) => t.id === id);

  const effectiveRoomOf = (c) => {
    if (c.room_number) return c.room_number;
    const t = (c.assigned_teacher_ids || []).map(teacherById).filter(Boolean).find((x) => x.room_number);
    return t?.room_number || null;
  };

  const filteredChildren = useMemo(() => {
    const term = q.trim().toLowerCase();
    return children.filter((c) => {
      if (term && !(c.name || "").toLowerCase().includes(term)) return false;
      if (roomFilter !== "all") {
        const r = effectiveRoomOf(c);
        if (roomFilter === "none") { if (r) return false; }
        else { if (String(r) !== roomFilter) return false; }
      }
      if (teacherFilter !== "all") {
        const tids = c.assigned_teacher_ids || [];
        if (teacherFilter === "none") { if (tids.length > 0) return false; }
        else { if (!tids.includes(teacherFilter)) return false; }
      }
      return true;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children, teachers, q, roomFilter, teacherFilter]);

  const hasActiveFilters = q.trim() !== "" || roomFilter !== "all" || teacherFilter !== "all";
  const clearFilters = () => { setQ(""); setRoomFilter("all"); setTeacherFilter("all"); };

  const applyPreset = (p) => {
    const f = p.filters || {};
    setQ(f.q || "");
    setRoomFilter(f.roomFilter || "all");
    setTeacherFilter(f.teacherFilter || "all");
    toast.success(`تم تطبيق: ${p.name}`);
  };

  const savePreset = async () => {
    const name = window.prompt("اسم الإعداد المسبق (مثال: قاعة 3 · أ. أحمد):", "");
    if (!name || !name.trim()) return;
    try {
      const { data } = await api.post("/admin/filter-presets", {
        name: name.trim(),
        scope: "students",
        filters: { q, roomFilter, teacherFilter },
      });
      toast.success(`💾 حُفظ "${data.name}"`);
      const r = await api.get("/admin/filter-presets", { params: { scope: "students" } });
      setPresets(r.data || []);
    } catch (e) { toast.error(extractError(e)); }
  };

  const deletePreset = async (p) => {
    if (!window.confirm(`حذف "${p.name}"؟`)) return;
    try {
      await api.delete(`/admin/filter-presets/${p.id}`);
      setPresets((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("تم الحذف");
    } catch (e) { toast.error(extractError(e)); }
  };

  const downloadIdCard = async (c) => {
    try {
      const r = await api.get(`/children/${c.id}/id-card`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `student-card-${c.name.replace(/\s+/g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("تم توليد بطاقة الطالب");
    } catch (e) { toast.error(extractError(e)); }
  };

  const batchPrintCards = async () => {
    // Prefer explicit room filter; otherwise ask
    let roomNum = null;
    if (roomFilter !== "all" && roomFilter !== "none") roomNum = Number(roomFilter);
    else {
      const val = window.prompt("اطبع بطاقات لأي قاعة؟ أدخل رقم القاعة (1-6):", "1");
      if (!val) return;
      const n = Number(val);
      if (!(n >= 1 && n <= 6)) { toast.error("رقم قاعة غير صالح"); return; }
      roomNum = n;
    }
    try {
      const r = await api.post("/admin/room-id-cards", { room_number: roomNum }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `student-cards-room-${roomNum}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`🖨️ تم توليد ملف بطاقات قاعة ${roomNum}`);
    } catch (e) { toast.error(extractError(e)); }
  };

  const togglePresetPublic = async (p) => {
    try {
      const { data } = await api.patch(`/admin/filter-presets/${p.id}`, { is_public: !p.is_public });
      setPresets((prev) => prev.map((x) => x.id === p.id ? { ...x, is_public: data.is_public } : x));
      toast.success(data.is_public ? "🌐 مُشترك مع الفريق" : "🔒 خاص بك");
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="children-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900">
            {user.role === "parent" ? "ملف طالبي" : user.role === "teacher" ? "طلابي" : "الطلاب"}
          </h1>
          <p className="text-slate-500 mt-2">{hasActiveFilters ? `${filteredChildren.length} من ${children.length}` : `${children.length} طالب مسجل`}</p>
        </div>
        {user.role === "admin" && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setBulkRoomOpen(true)}
              className="h-12 gap-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              data-testid="bulk-room-btn"
            >
              <DoorOpen className="w-4 h-4" /> إسناد قاعة جماعي
            </Button>
            <Button
              variant="outline"
              onClick={batchPrintCards}
              className="h-12 gap-2 border-sky-200 text-sky-700 hover:bg-sky-50"
              data-testid="batch-cards-btn"
              title="طباعة بطاقات ID لكل طلاب قاعة معيّنة على شكل ورقة A4"
            >
              <Printer className="w-4 h-4" /> بطاقات قاعة
            </Button>
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button className="h-12 gap-2 bg-sky-600 hover:bg-sky-700" data-testid="add-child-btn">
                  <Plus className="w-4 h-4" /> إضافة طالب
                </Button>
              </DialogTrigger>
              <ChildFormDialog
                mode="add"
                initial={empty}
                parents={parents}
                teachers={teachers}
                onClose={() => setAddOpen(false)}
                onSaved={() => { setAddOpen(false); load(); }}
              />
            </Dialog>
          </div>
        )}
      </div>

      {/* Search & Filters */}
      {user.role !== "parent" && children.length > 0 && (
        <Card className="border-slate-200/70" data-testid="student-filters">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
              <div className="md:col-span-6 relative">
                <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  dir="rtl"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="ابحث باسم الطالب..."
                  className="pr-9 h-11"
                  data-testid="student-search"
                />
              </div>
              <div className="md:col-span-3">
                <Select value={roomFilter} onValueChange={setRoomFilter}>
                  <SelectTrigger className="h-11" data-testid="student-filter-room">
                    <div className="flex items-center gap-2">
                      <DoorOpen className="w-4 h-4 text-indigo-600" />
                      <SelectValue placeholder="القاعة" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">كل القاعات</SelectItem>
                    <SelectItem value="none">بدون قاعة</SelectItem>
                    {[1, 2, 3, 4, 5, 6].map((n) => <SelectItem key={n} value={String(n)}>قاعة {n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-3">
                <Select value={teacherFilter} onValueChange={setTeacherFilter}>
                  <SelectTrigger className="h-11" data-testid="student-filter-teacher">
                    <div className="flex items-center gap-2">
                      <GraduationCap className="w-4 h-4 text-emerald-600" />
                      <SelectValue placeholder="المعلم" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">كل المعلمين</SelectItem>
                    <SelectItem value="none">بدون معلم</SelectItem>
                    {teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{t.room_number ? ` — قاعة ${t.room_number}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {hasActiveFilters && (
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span className="text-[11px] font-black text-sky-700 bg-sky-50 rounded-full px-2 py-0.5 flex items-center gap-1">
                  <Filter className="w-3 h-3" /> فلاتر نشطة
                </span>
                <Button size="sm" variant="outline" onClick={clearFilters} className="h-7 gap-1 text-[11px] text-rose-600 border-rose-200" data-testid="student-filters-clear">
                  <X className="w-3 h-3" /> مسح كل الفلاتر
                </Button>
                {user.role === "admin" && (
                  <Button size="sm" variant="outline" onClick={savePreset} className="h-7 gap-1 text-[11px] text-emerald-700 border-emerald-200" data-testid="filter-save-preset">
                    <Bookmark className="w-3 h-3" /> حفظ كإعداد مسبق
                  </Button>
                )}
              </div>
            )}
            {user.role === "admin" && presets.length > 0 && (
              <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="filter-presets-list">
                <span className="text-[11px] font-black text-slate-500">إعدادات محفوظة:</span>
                {presets.map((p) => (
                  <span key={p.id} className={`inline-flex items-center gap-1 rounded-full pl-1 pr-2 py-0.5 text-[11px] font-bold ${p.is_public ? "bg-blue-50 border border-blue-200 text-blue-800" : "bg-emerald-50 border border-emerald-200 text-emerald-800"}`} data-testid={`filter-preset-${p.id}`} title={p.is_public && !p.is_mine ? `مشترك بواسطة ${p.owner_name || "زميل"}` : ""}>
                    <button type="button" onClick={() => applyPreset(p)} className="px-1.5 py-0.5 rounded-full hover:bg-white/50" data-testid={`filter-preset-apply-${p.id}`}>
                      {p.is_public ? "🌐" : "📌"} {p.name}
                    </button>
                    {p.is_mine && (
                      <button
                        type="button"
                        onClick={() => togglePresetPublic(p)}
                        className={`rounded-full p-0.5 hover:bg-white/70 ${p.is_public ? "text-blue-700" : "text-slate-500"}`}
                        title={p.is_public ? "إلغاء المشاركة (سيصبح خاصاً)" : "مشاركة مع الفريق"}
                        data-testid={`filter-preset-toggle-${p.id}`}
                      >
                        {p.is_public ? <Globe className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                      </button>
                    )}
                    {p.is_mine && (
                      <button type="button" onClick={() => deletePreset(p)} className="text-rose-600 hover:bg-rose-100 rounded-full p-0.5" data-testid={`filter-preset-delete-${p.id}`} title="حذف">
                        <X className="w-3 h-3" />
                      </button>
                    )}
                    {!p.is_mine && p.owner_name && (
                      <span className="text-[9px] text-blue-600 px-1">— {p.owner_name}</span>
                    )}
                  </span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {children.length === 0 ? (
        <Card><CardContent className="p-16 text-center text-slate-400">لا يوجد طلاب مسجلون بعد</CardContent></Card>
      ) : filteredChildren.length === 0 ? (
        <Card><CardContent className="p-16 text-center text-slate-400 space-y-2">
          <div className="text-4xl">🔍</div>
          <div>لا توجد نتائج مطابقة للفلاتر</div>
          <Button size="sm" variant="outline" onClick={clearFilters} data-testid="empty-clear-filters">إعادة تعيين الفلاتر</Button>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredChildren.map((c) => {
            const kidTeachers = (c.assigned_teacher_ids || []).map(teacherById).filter(Boolean);
            const roomFromTeacher = kidTeachers.map((t) => t.room_number).find(Boolean);
            const effectiveRoom = c.room_number || roomFromTeacher || null;
            return (
            <Card key={c.id} data-testid={`child-card-${c.id}`} className="card-elevated border-slate-200/70 hover:border-sky-300 transition-all overflow-hidden">
              <div className="h-24 relative overflow-hidden bg-gradient-to-br from-sky-100 to-emerald-100">
                <ChildPhoto child={c} className="w-full h-full object-cover opacity-60" />
                <div className="absolute inset-0 bg-gradient-to-t from-white via-white/20 to-transparent" />
              </div>
              <CardContent className="p-6 pt-0 -mt-12 relative">
                <div className="flex items-start gap-4 mb-4">
                  <div className="relative">
                    <ChildPhoto child={c} className="w-16 h-16 rounded-2xl object-cover ring-4 ring-white shadow-md" />
                    {(user.role === "admin" || user.role === "teacher") && (
                      <PhotoUploadBadge child={c} onDone={load} />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="text-xl font-black text-slate-900 flex items-center gap-2 flex-wrap">
                      {c.name}
                      {(c.mastered_skills?.length || 0) > 0 && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-l from-amber-400 to-yellow-500 text-white text-[10px] font-black shadow"
                          title={`${c.mastered_skills.length} مهارة متقنة مدى الحياة`}
                          data-testid={`mastered-badge-${c.id}`}
                        >
                          🏆 {c.mastered_skills.length}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-500 mt-1 flex items-center gap-2">
                      <Baby className="w-3.5 h-3.5" />
                      {c.gender === "male" ? "ذكر" : "أنثى"} · {c.date_of_birth}
                    </div>
                  </div>
                  {user.role === "admin" && (
                    <button
                      onClick={() => setEditing(c)}
                      className="w-9 h-9 rounded-full bg-white border-2 border-sky-200 text-sky-700 hover:bg-sky-50 hover:border-sky-400 flex items-center justify-center shadow-sm transition"
                      title="تعديل بيانات الطالب"
                      data-testid={`edit-child-${c.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Room + Teachers strip */}
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  {effectiveRoom ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-800 text-[11px] font-black" data-testid={`child-room-${c.id}`}>
                      <DoorOpen className="w-3 h-3" /> قاعة {effectiveRoom}
                    </span>
                  ) : user.role === "admin" ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 text-[11px] font-bold" data-testid={`child-room-${c.id}`}>
                      <DoorOpen className="w-3 h-3" /> بدون قاعة
                    </span>
                  ) : null}
                  {kidTeachers.length > 0 ? kidTeachers.slice(0, 3).map((t) => (
                    <span key={t.id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-bold" data-testid={`child-teacher-${c.id}-${t.id}`}>
                      <GraduationCap className="w-3 h-3" /> {t.name}
                    </span>
                  )) : user.role === "admin" ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-500 text-[11px] font-bold">
                      <GraduationCap className="w-3 h-3" /> بدون معلم
                    </span>
                  ) : null}
                  {kidTeachers.length > 3 && (
                    <span className="text-[11px] font-bold text-slate-500">+{kidTeachers.length - 3}</span>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <InfoRow label="التشخيص" value={c.disability_type} />
                  {c.emergency_contact_phone && (
                    <InfoRow icon={Phone} label="طوارئ" value={<span dir="ltr">{c.emergency_contact_phone}</span>} />
                  )}
                  {c.medications && <InfoRow icon={Pill} label="أدوية" value={c.medications} />}
                  {c.allergies && <InfoRow icon={AlertTriangle} label="حساسية" value={c.allergies} tone="rose" />}
                </div>
                {canTriggerEmergency && (
                  <Button
                    data-testid={`emergency-btn-${c.id}`}
                    onClick={() => setEmergency({ child: c, kind: "medical", message: "" })}
                    className="w-full mt-4 h-11 bg-rose-600 hover:bg-rose-700 text-white gap-2 font-bold"
                  >
                    <Siren className="w-4 h-4" /> زر الطوارئ
                  </Button>
                )}
                <Button
                  data-testid={`open-gallery-${c.id}`}
                  onClick={() => setGalleryFor(c)}
                  variant="outline"
                  className="w-full mt-2 h-11 gap-2 text-sky-700 border-sky-200 hover:bg-sky-50"
                >
                  <Images className="w-4 h-4" /> معرض الصور
                </Button>
                {user.role === "admin" && (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <Button
                      data-testid={`id-card-${c.id}`}
                      onClick={() => downloadIdCard(c)}
                      variant="outline"
                      className="h-11 gap-2 text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                      title="طباعة بطاقة تعريفية للطالب"
                    >
                      <IdCard className="w-4 h-4" /> بطاقة PDF
                    </Button>
                    <Button
                      data-testid={`room-history-${c.id}`}
                      onClick={() => setRoomHistoryFor(c)}
                      variant="outline"
                      className="h-11 gap-2 text-slate-700 border-slate-200 hover:bg-slate-50"
                      title="سجل تنقّلات القاعة"
                    >
                      <History className="w-4 h-4" /> سجل القاعات
                    </Button>
                  </div>
                )}
                {(c.certificates_history?.length || 0) > 0 && (
                  <Button
                    data-testid={`open-cert-history-${c.id}`}
                    onClick={() => setCertHistoryFor(c)}
                    variant="outline"
                    className="w-full mt-2 h-11 gap-2 text-amber-700 border-amber-300 hover:bg-amber-50 bg-gradient-to-l from-amber-50/60 to-white"
                  >
                    <Award className="w-4 h-4" /> سجل شهادات الإنجاز
                    <span className="mr-auto px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-black">{c.certificates_history.length}</span>
                  </Button>
                )}
                <div className="mt-3">
                  <ProgressLineChart childId={c.id} compact />
                </div>
              </CardContent>
            </Card>
          );})}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        {editing && (
          <ChildFormDialog
            mode="edit"
            initial={{
              name: editing.name || "",
              date_of_birth: editing.date_of_birth || "",
              gender: editing.gender || "male",
              disability_type: editing.disability_type || "",
              medications: editing.medications || "",
              allergies: editing.allergies || "",
              emergency_contact_name: editing.emergency_contact_name || "",
              emergency_contact_phone: editing.emergency_contact_phone || "",
              parent_id: editing.parent_id || "",
              assigned_teacher_ids: editing.assigned_teacher_ids || [],
              notes: editing.notes || "",
              room_number: editing.room_number || null,
              photo_url: editing.photo_url || null,
              photo_path: editing.photo_path || null,
            }}
            childId={editing.id}
            parents={parents}
            teachers={teachers}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}
      </Dialog>

      <EmergencyDialog emergency={emergency} setEmergency={setEmergency} />
      <ChildGalleryDialog
        child={galleryFor}
        open={!!galleryFor}
        onOpenChange={(v) => !v && setGalleryFor(null)}
      />
      <CertificateHistoryDialog
        open={!!certHistoryFor}
        onClose={() => setCertHistoryFor(null)}
        child={certHistoryFor}
      />
      <BulkRoomAssignDialog
        open={bulkRoomOpen}
        onClose={() => setBulkRoomOpen(false)}
        children={children}
        teachers={teachers}
        onSaved={() => { setBulkRoomOpen(false); load(); }}
      />
      <RoomHistoryDialog
        child={roomHistoryFor}
        open={!!roomHistoryFor}
        onClose={() => setRoomHistoryFor(null)}
      />
    </div>
  );
}

function ChildFormDialog({ mode, initial, childId, parents, teachers, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isEdit = mode === "edit";

  const toggleTeacher = (tid) => {
    setForm((f) => {
      const has = (f.assigned_teacher_ids || []).includes(tid);
      const next = has
        ? f.assigned_teacher_ids.filter((x) => x !== tid)
        : [...(f.assigned_teacher_ids || []), tid];
      return { ...f, assigned_teacher_ids: next };
    });
  };

  // Auto-fill: when a room is picked, sort teachers so matching-room teachers appear on top.
  const sortedTeachers = useMemo(() => {
    const room = form.room_number;
    if (!room) return teachers;
    const match = teachers.filter((t) => Number(t.room_number) === Number(room));
    const rest = teachers.filter((t) => Number(t.room_number) !== Number(room));
    return [...match, ...rest];
  }, [teachers, form.room_number]);

  const suggestedIds = useMemo(() => {
    if (!form.room_number) return new Set();
    return new Set(teachers.filter((t) => Number(t.room_number) === Number(form.room_number)).map((t) => t.id));
  }, [teachers, form.room_number]);

  const save = async () => {
    if (!form.name?.trim() || !form.date_of_birth?.trim() || !form.disability_type?.trim() || !form.emergency_contact_name?.trim() || !form.emergency_contact_phone?.trim()) {
      toast.error("املأ الحقول المطلوبة (الاسم، الميلاد، التشخيص، بيانات الطوارئ)");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, room_number: form.room_number || null };
      if (isEdit) {
        await api.put(`/children/${childId}`, payload);
        toast.success("تم تحديث بيانات الطالب");
      } else {
        await api.post("/children", payload);
        toast.success("تم إضافة الطالب");
      }
      onSaved?.();
    } catch (e) {
      toast.error(extractError(e));
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!childId) return;
    if (!window.confirm(`حذف الطالب "${initial.name}" نهائياً؟\nهذا الإجراء لا يمكن التراجع عنه وسيؤدي إلى حذف كل بياناته.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/children/${childId}`);
      toast.success("تم حذف الطالب");
      onSaved?.();
    } catch (e) { toast.error(extractError(e)); }
    finally { setDeleting(false); }
  };

  return (
    <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid={isEdit ? "edit-child-dialog" : "add-child-dialog"}>
      <DialogHeader>
        <DialogTitle className="text-2xl font-black flex items-center gap-2">
          {isEdit ? <><Pencil className="w-5 h-5 text-sky-600" /> تعديل بيانات الطالب — {initial.name}</> : "إضافة طالب جديد"}
        </DialogTitle>
      </DialogHeader>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <Field label="الاسم الكامل *">
          <Input data-testid="child-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="تاريخ الميلاد *">
          <Input data-testid="child-dob" type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} />
        </Field>
        <Field label="الجنس">
          <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
            <SelectTrigger data-testid="child-gender"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="male">ذكر</SelectItem>
              <SelectItem value="female">أنثى</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="نوع الإعاقة / التشخيص *">
          <Input data-testid="child-disability" value={form.disability_type} onChange={(e) => setForm({ ...form, disability_type: e.target.value })} />
        </Field>
        <Field label="ولي الأمر">
          <Select value={form.parent_id || "__none"} onValueChange={(v) => setForm({ ...form, parent_id: v === "__none" ? "" : v })}>
            <SelectTrigger data-testid="child-parent"><SelectValue placeholder="اختر ولي الأمر" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">— بدون —</SelectItem>
              {parents.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="القاعة">
          <Select value={form.room_number ? String(form.room_number) : "__none"} onValueChange={(v) => setForm({ ...form, room_number: v === "__none" ? null : Number(v) })}>
            <SelectTrigger data-testid="child-room"><SelectValue placeholder="اختر القاعة" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">— بدون قاعة (تلقائي حسب المعلم) —</SelectItem>
              {ROOM_OPTIONS.map((n) => <SelectItem key={n} value={String(n)}>قاعة {n}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-[10px] text-slate-500 mt-1">يمكن ترك الحقل فارغاً ليتم تحديد القاعة تلقائياً من قاعة المعلم المسند.</div>
        </Field>

        {/* Multi-select teachers */}
        <div className="space-y-1.5 md:col-span-2">
          <Label className="text-sm font-bold text-slate-700 flex items-center gap-2">
            <GraduationCap className="w-4 h-4 text-emerald-600" /> المعلمون المسندون ({(form.assigned_teacher_ids || []).length})
          </Label>
          <div className="rounded-2xl border-2 border-emerald-100 bg-emerald-50/40 p-2 max-h-56 overflow-y-auto space-y-1.5" data-testid="child-teachers-list">
            {sortedTeachers.length === 0 ? (
              <div className="text-xs text-slate-400 text-center py-6">لا يوجد معلمون</div>
            ) : sortedTeachers.map((t) => {
              const on = (form.assigned_teacher_ids || []).includes(t.id);
              const suggested = suggestedIds.has(t.id);
              return (
                <label
                  key={t.id}
                  className={`flex items-center gap-3 p-2.5 rounded-xl border cursor-pointer transition ${on ? "border-emerald-300 bg-white ring-1 ring-emerald-200" : suggested ? "border-emerald-200 bg-white" : "border-transparent bg-white/70 hover:border-emerald-200"}`}
                  data-testid={`child-teacher-option-${t.id}`}
                >
                  <input type="checkbox" checked={on} onChange={() => toggleTeacher(t.id)} className="w-4 h-4 accent-emerald-600" />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-slate-900 truncate flex items-center gap-1.5">
                      {t.name}
                      {suggested && (
                        <span className="text-[9px] font-black text-emerald-800 bg-emerald-100 border border-emerald-300 px-1.5 py-0.5 rounded-full" title="معلم في نفس القاعة المختارة" data-testid={`teacher-suggested-${t.id}`}>
                          مطابق للقاعة
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">{t.job_title || "معلم"}{t.room_number ? ` · قاعة ${t.room_number}` : ""}</div>
                  </div>
                  {on && <Check className="w-4 h-4 text-emerald-600" />}
                </label>
              );
            })}
          </div>
        </div>

        <Field label="اسم جهة الطوارئ *">
          <Input data-testid="child-emergency-name" value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} />
        </Field>
        <Field label="هاتف الطوارئ *">
          <Input dir="ltr" data-testid="child-emergency-phone" value={form.emergency_contact_phone} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} />
        </Field>
        <Field label="الأدوية" full>
          <Textarea value={form.medications} onChange={(e) => setForm({ ...form, medications: e.target.value })} />
        </Field>
        <Field label="الحساسيات" full>
          <Textarea value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} />
        </Field>
        <Field label="ملاحظات" full>
          <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </Field>
      </div>
      <div className="flex justify-between items-center gap-2 mt-6 pt-4 border-t border-slate-100">
        <div>
          {isEdit && (
            <Button onClick={remove} disabled={saving || deleting} className="h-10 gap-2 bg-rose-600 hover:bg-rose-700" data-testid="delete-child-btn">
              <Trash2 className="w-4 h-4" /> {deleting ? "جارٍ الحذف..." : "حذف"}
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving || deleting}>إلغاء</Button>
          <Button data-testid="save-child-btn" disabled={saving || deleting} className="bg-sky-600 hover:bg-sky-700" onClick={save}>
            {saving ? "جاري الحفظ..." : isEdit ? "تعديل" : "حفظ"}
          </Button>
        </div>
      </div>
    </DialogContent>
  );
}

function EmergencyDialog({ emergency, setEmergency }) {
  const [sending, setSending] = useState(false);
  if (!emergency) return null;
  const send = async () => {
    if (!emergency.message.trim()) { toast.error("اكتب وصفاً موجزاً"); return; }
    setSending(true);
    try {
      await api.post("/emergency", {
        child_id: emergency.child.id,
        kind: emergency.kind,
        message: emergency.message.trim(),
      });
      toast.success("تم إرسال التنبيه — الأهل والإدارة تم إشعارهم فوراً");
      setEmergency(null);
    } catch (e) { toast.error(extractError(e)); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={!!emergency} onOpenChange={(v) => !v && setEmergency(null)}>
      <DialogContent data-testid="emergency-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black text-rose-700 flex items-center gap-2">
            <Siren className="w-6 h-6" /> تنبيه طوارئ فوري
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-900">
            سيتم إرسال إشعار واتساب فوري لولي أمر <span className="font-bold">{emergency.child.name}</span> وللإدارة.
          </div>
          <div className="space-y-1.5">
            <Label className="font-bold">نوع الحالة</Label>
            <Select value={emergency.kind} onValueChange={(v) => setEmergency({ ...emergency, kind: v })}>
              <SelectTrigger data-testid="emergency-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="medical">حالة طبية</SelectItem>
                <SelectItem value="injury">إصابة</SelectItem>
                <SelectItem value="behavioral">حالة سلوكية</SelectItem>
                <SelectItem value="other">أخرى</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="font-bold">التفاصيل</Label>
            <Textarea
              data-testid="emergency-message"
              rows={4}
              value={emergency.message}
              onChange={(e) => setEmergency({ ...emergency, message: e.target.value })}
              placeholder="مثال: ارتفاع مفاجئ في الحرارة، يحتاج توجه فوري"
            />
          </div>
          <Button
            data-testid="send-emergency-btn"
            onClick={send}
            disabled={sending}
            className="w-full h-12 bg-rose-600 hover:bg-rose-700 gap-2 font-bold"
          >
            <Siren className="w-5 h-5" /> {sending ? "جاري الإرسال..." : "إرسال التنبيه الآن"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={`space-y-1.5 ${full ? "md:col-span-2" : ""}`}>
      <Label className="text-sm font-bold text-slate-700">{label}</Label>
      {children}
    </div>
  );
}
function InfoRow({ icon: Icon, label, value, tone }) {
  return (
    <div className={`flex items-center gap-2 ${tone === "rose" ? "text-rose-700" : "text-slate-700"}`}>
      {Icon && <Icon className="w-3.5 h-3.5 shrink-0" />}
      <span className="text-slate-500">{label}:</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}

function RoomHistoryDialog({ child, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open || !child) return;
    setLoading(true);
    api.get(`/admin/children/${child.id}/room-history`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(extractError(e)))
      .finally(() => setLoading(false));
  }, [open, child]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="room-history-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-black flex items-center gap-2">
            <History className="w-5 h-5 text-indigo-600" /> سجل تنقّلات القاعة — {child?.name || ""}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="text-center py-10 text-slate-400 text-sm">جارٍ التحميل...</div>
        ) : data ? (
          <div className="space-y-3">
            <div className="rounded-2xl bg-indigo-50 border border-indigo-200 p-3 text-center" data-testid="room-history-current">
              <div className="text-[10px] font-black text-indigo-700">القاعة الحالية</div>
              <div className="text-2xl font-black text-indigo-900 mt-1">
                {data.current_room ? `قاعة ${data.current_room}` : "بدون قاعة"}
              </div>
            </div>
            <div className="text-xs font-black text-slate-700 mt-2">
              📜 السجل ({data.history?.length || 0} تعديل)
            </div>
            {(data.history?.length || 0) === 0 ? (
              <div className="text-center py-6 text-slate-400 text-xs">لم تُسجَّل تنقّلات بعد</div>
            ) : (
              <>
                <RoomTimeline history={data.history} current={data.current_room} />
                <div className="space-y-2 max-h-[35vh] overflow-y-auto pr-1 mt-3">
                  {data.history.map((h, i) => (
                    <div key={i} className="p-3 rounded-xl bg-white border border-slate-200 flex items-center gap-3 text-sm" data-testid={`room-history-item-${i}`}>
                      <div className="text-2xl">🚪</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-slate-900">
                          {h.from ? `قاعة ${h.from}` : "بدون"} ← {h.to ? `قاعة ${h.to}` : "بدون"}
                        </div>
                        <div className="text-[10px] text-slate-500 mt-0.5">
                          {new Date(h.changed_at).toLocaleString("ar", { dateStyle: "medium", timeStyle: "short" })} · بواسطة {h.changed_by_name || "—"}
                          {h.source === "bulk_assign" && <span className="mr-1.5 inline-block px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[9px] font-black">جماعي</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

const ROOM_COLORS = { 1: "#0EA5E9", 2: "#10B981", 3: "#F59E0B", 4: "#8B5CF6", 5: "#EC4899", 6: "#EF4444" };
function RoomTimeline({ history, current }) {
  // Build segments: [{room, start, end}] using changed_at as boundaries. History is newest-first.
  const asc = [...history].reverse();
  if (asc.length === 0) return null;
  const now = Date.now();
  const segments = [];
  // Initial "from" if any
  const firstFrom = asc[0].from;
  const firstAt = new Date(asc[0].changed_at).getTime();
  const earliest = Math.min(...asc.map((h) => new Date(h.changed_at).getTime()));
  const span = Math.max(now - earliest, 1000 * 60 * 60 * 24 * 7); // at least 1 week span
  if (firstFrom) {
    // Assume the "from" room started 1 week before the first log
    segments.push({ room: firstFrom, start: earliest - 1000 * 60 * 60 * 24 * 7, end: firstAt });
  }
  asc.forEach((h, i) => {
    const start = new Date(h.changed_at).getTime();
    const end = i + 1 < asc.length ? new Date(asc[i + 1].changed_at).getTime() : now;
    segments.push({ room: h.to, start, end });
  });
  const timelineStart = segments[0].start;
  const timelineEnd = now;
  const totalMs = Math.max(timelineEnd - timelineStart, 1);
  const fmt = (t) => new Date(t).toLocaleDateString("ar", { month: "short", day: "numeric", year: "2-digit" });
  return (
    <div className="mt-4" data-testid="room-timeline">
      <div className="text-xs font-black text-slate-700 mb-2">📅 الخط الزمني</div>
      <div className="relative h-16 rounded-2xl bg-slate-50 border border-slate-200 overflow-hidden">
        {segments.map((seg, i) => {
          const left = ((seg.start - timelineStart) / totalMs) * 100;
          const width = ((seg.end - seg.start) / totalMs) * 100;
          const color = seg.room ? ROOM_COLORS[seg.room] : "#94A3B8";
          return (
            <div
              key={i}
              className="absolute top-2 bottom-2 flex items-center justify-center text-white text-[10px] font-black rounded-md"
              style={{ right: `${left}%`, width: `${Math.max(width, 2)}%`, background: color, boxShadow: "0 1px 3px rgba(0,0,0,0.15)" }}
              title={`${seg.room ? "قاعة " + seg.room : "بدون"} — ${fmt(seg.start)} إلى ${fmt(seg.end)}`}
              data-testid={`timeline-seg-${i}`}
            >
              {width > 8 ? (seg.room ? `${seg.room}` : "—") : ""}
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between text-[10px] text-slate-500 font-bold mt-1">
        <span dir="ltr">{fmt(timelineStart)}</span>
        <span dir="ltr">الآن</span>
      </div>
      {/* Legend */}
      <div className="flex items-center gap-2 flex-wrap mt-2 text-[10px] font-bold">
        {Object.entries(ROOM_COLORS).map(([n, c]) => (
          <span key={n} className="inline-flex items-center gap-1">
            <span className="w-3 h-3 rounded" style={{ background: c }} /> قاعة {n}
          </span>
        ))}
      </div>
    </div>
  );
}

function PhotoUploadBadge({ child, onDone }) {
  const [uploading, setUploading] = useState(false);
  const onPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("الحد الأقصى ٨ ميغابايت"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/children/${child.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("تم رفع الصورة");
      await onDone?.();
    } catch (e) { toast.error(extractError(e)); }
    finally { setUploading(false); }
  };
  return (
    <label
      data-testid={`upload-photo-${child.id}`}
      className="absolute -bottom-1 -left-1 w-7 h-7 rounded-full bg-sky-600 hover:bg-sky-700 text-white flex items-center justify-center shadow-md cursor-pointer ring-2 ring-white"
      title="رفع صورة"
    >
      <Camera className="w-3.5 h-3.5" />
      <input type="file" accept="image/*" className="hidden" onChange={onPick} disabled={uploading} />
    </label>
  );
}
